/* ════════════════════════════════════════════════════════════════════
   Icon set for the replay HUD (PlayerBottomBar + top command bar).
   Two voices: blocky pixelarticons-style fills for the transport
   glyphs, and Tabler-style rounded strokes for utility controls.
   ──────────────────────────────────────────────────────────────────── */

import React from 'react'

interface IconProps {
  size?: number | string
  className?: string
}

const svgProps = (extra: React.SVGAttributes<SVGSVGElement> = {}) => ({
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  ...extra,
})

/* Shared config for the Tabler-style stroke icons. */
const strokeProps = () => svgProps({
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
})

/** Play — pixelarticons "play" path (blocky right-pointing triangle). */
export const PlayIcon = ({ size = 16, className }: IconProps) => (
  <svg width={size} height={size} className={className} {...svgProps()} aria-hidden="true">
    <path d="M10 20H8V4h2v2h2v3h2v2h2v2h-2v2h-2v3h-2v2z" />
  </svg>
)

/** Pause — pixelarticons "pause" path (two blocky bars). */
export const PauseIcon = ({ size = 16, className }: IconProps) => (
  <svg width={size} height={size} className={className} {...svgProps()} aria-hidden="true">
    <path d="M10 4H5v16h5V4zm9 0h-5v16h5V4z" />
  </svg>
)

/** Tabler Icons: rewind-backward-10. */
export const SkipBack10Icon = ({ size = 16, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    className={className}
    {...svgProps({
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.7,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    })}
    aria-hidden="true"
  >
    <path d="M7 9l-3-3 3-3" />
    <path d="M15.997 17.918A6.002 6.002 0 0 0 15 6H4" />
    <path d="M6 14v6" />
    <path d="M9 15.5v3a1.5 1.5 0 0 0 3 0v-3a1.5 1.5 0 0 0-3 0" />
  </svg>
)

/** Tabler Icons: rewind-forward-10. */
export const SkipForward10Icon = ({ size = 16, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    className={className}
    {...svgProps({
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.7,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    })}
    aria-hidden="true"
  >
    <path d="M17 9l3-3-3-3" />
    <path d="M8 17.918A5.997 5.997 0 0 1 3 12a6 6 0 0 1 6-6h11" />
    <path d="M12 14v6" />
    <path d="M15 15.5v3a1.5 1.5 0 0 0 3 0v-3a1.5 1.5 0 0 0-3 0" />
  </svg>
)

/**
 * Restart — skip-to-start (⏮): chunky vertical bar + left-pointing triangle.
 * Based on pixelarticons "prev" but with a thicker 3px bar so it reads as a
 * solid Minecraft-style block rather than a thin line at small sizes. The
 * triangle keeps absolute coords so the wider bar doesn't shift it.
 */
export const RestartIcon = ({ size = 16, className }: IconProps) => (
  <svg width={size} height={size} className={className} {...svgProps()} aria-hidden="true">
    <path d="M5 4h3v16H5V4zM18 4h-2v2h-2v3h-2v2h-2v2h2v3h2v2h2v2h2V4z" />
  </svg>
)

/**
 * Overview toggle — opens the secondary (range) timeline. The glyph is a
 * literal picture of the feature: the main timeline on top with its playhead,
 * and a second rail below flanked by the two range-selection handles — the
 * same handles the overview track actually shows. Replaces the generic
 * "sliders" icon, which read as settings, not as a second timeline.
 */
export const OverviewIcon = ({ size = 16, className }: IconProps) => (
  <svg width={size} height={size} className={className} {...svgProps()} aria-hidden="true">
    <path d="M3 6h18v2H3zM11 3h2v8h-2zM3 16h18v2H3zM7 13h2v8H7zM15 13h2v8h-2z" />
  </svg>
)

/* ── Top command bar (Tabler-style strokes) ─────────────────────────── */

/** Tabler Icons: list-details — the packet list toggle. */
export const PacketListIcon = ({ size = 16, className }: IconProps) => (
  <svg width={size} height={size} className={className} {...strokeProps()} aria-hidden="true">
    <path d="M13 5h8" />
    <path d="M13 9h5" />
    <path d="M13 15h8" />
    <path d="M13 19h5" />
    <path d="M3 4m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" />
    <path d="M3 14m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" />
  </svg>
)

/** Tabler Icons: eye — HUD visible. */
export const EyeIcon = ({ size = 16, className }: IconProps) => (
  <svg width={size} height={size} className={className} {...strokeProps()} aria-hidden="true">
    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
  </svg>
)

/** Tabler Icons: eye-off — HUD hidden. */
export const EyeOffIcon = ({ size = 16, className }: IconProps) => (
  <svg width={size} height={size} className={className} {...strokeProps()} aria-hidden="true">
    <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" />
    <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87" />
    <path d="M3 3l18 18" />
  </svg>
)

/** Tabler Icons: keyboard — the hotkeys sheet. */
export const KeyboardIcon = ({ size = 16, className }: IconProps) => (
  <svg width={size} height={size} className={className} {...strokeProps()} aria-hidden="true">
    <path d="M2 6m0 2a2 2 0 0 1 2 -2h16a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-16a2 2 0 0 1 -2 -2z" />
    <path d="M6 10l0 .01" />
    <path d="M10 10l0 .01" />
    <path d="M14 10l0 .01" />
    <path d="M18 10l0 .01" />
    <path d="M6 14l0 .01" />
    <path d="M18 14l0 .01" />
    <path d="M10 14l4 .01" />
  </svg>
)

/** Tabler Icons: adjustments-horizontal — replay settings. */
export const SlidersIcon = ({ size = 16, className }: IconProps) => (
  <svg width={size} height={size} className={className} {...strokeProps()} aria-hidden="true">
    <path d="M14 6m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
    <path d="M4 6l8 0" />
    <path d="M16 6l4 0" />
    <path d="M8 12m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
    <path d="M4 12l2 0" />
    <path d="M10 12l10 0" />
    <path d="M17 18m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
    <path d="M4 18l11 0" />
    <path d="M19 18l1 0" />
  </svg>
)
